/**
 * Control segmentado único de la app móvil (B8 · H12). Antes había tres
 * estilos (píldoras oscuras en la ficha, fondos de color en el editor de
 * movimientos y el de patrimonio); ahora todos son así: carril `surface2`
 * y la opción activa en `surface` con sombra suave. Cada opción es un botón
 * con `aria-pressed` y `data-testid` `${testId}-${valor}`.
 */
import type { CSSProperties } from 'react';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { color, movimiento, TACTIL } from './tokens';

export type OpcionSegmentado<T extends string> = {
  valor: T;
  texto: string;
  /**
   * Tinte del texto cuando está activa (p. ej. rojo en «Gasto»). Solo el
   * texto: el fondo es siempre `surface`.
   */
  tinte?: string;
};

type SegmentadoProps<T extends string> = {
  /** Nombre del grupo para los lectores de pantalla. */
  etiqueta?: string;
  opciones: readonly OpcionSegmentado<T>[];
  valor: T;
  onChange: (valor: T) => void;
  /** Prefijo del `data-testid` de cada opción. */
  testId: string;
  isDisabled?: boolean;
  /** Las opciones se reparten el ancho (editor de movimientos). */
  estirar?: boolean;
  /** Alto visible de cada opción (la zona táctil es siempre ≥ 44 px). */
  alto?: number;
  style?: CSSProperties;
};

/** Sombra suave de la opción activa. */
export const SOMBRA_SEGMENTO = '0 1px 2px rgba(20,30,60,.12)';

/**
 * Zona táctil de cada opción (B2 · H04): crece en vertical hasta 44 px sin
 * ensanchar (entre opciones hay 2 px; un toque caería en la vecina).
 */
const ZONA = css({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    height: TACTIL,
    transform: 'translateY(-50%)',
    background: 'transparent',
  },
});

/** Color del texto de una opción (lógica pura, probada aparte). */
export function colorTextoSegmento(activa: boolean, tinte?: string): string {
  if (!activa) {
    return color.fg2;
  }
  return tinte ?? color.fg;
}

export function Segmentado<T extends string>({
  etiqueta,
  opciones,
  valor,
  onChange,
  testId,
  isDisabled,
  estirar = false,
  alto = 30,
  style,
}: SegmentadoProps<T>) {
  return (
    <View
      role="group"
      aria-label={etiqueta}
      data-testid={`${testId}-segmentado`}
      style={{
        flexDirection: 'row',
        gap: 2,
        padding: 3,
        borderRadius: 12,
        backgroundColor: color.surface2,
        ...(estirar ? { alignSelf: 'stretch' } : null),
        ...style,
      }}
    >
      {opciones.map(o => {
        const activa = o.valor === valor;
        return (
          <Button
            key={o.valor}
            variant="bare"
            aria-pressed={activa}
            isDisabled={isDisabled}
            data-testid={`${testId}-${o.valor}`}
            onPress={() => onChange(o.valor)}
            className={ZONA}
            style={{
              overflow: 'visible',
              ...(estirar ? { flex: 1 } : null),
              height: alto,
              padding: '0 10px',
              borderRadius: 9,
              fontSize: alto > 34 ? 13 : 12.5,
              fontWeight: 700,
              color: colorTextoSegmento(activa, o.tinte),
              backgroundColor: activa ? color.surface : 'transparent',
              boxShadow: activa ? SOMBRA_SEGMENTO : undefined,
              transition: `background-color ${movimiento.pildora}ms, color ${movimiento.pildora}ms`,
            }}
          >
            {o.texto}
          </Button>
        );
      })}
    </View>
  );
}
